import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle, X, Loader2, CheckCircle2, Filter, Wrench, Undo2, Printer, Sparkles, Tags,
} from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";
import useEscClose from "../hooks/useEscClose";
import { alertDialog, confirmDialog, promptDialog } from "../utils/dialogs";

const SEV_STYLE = {
  high:   { bg: "bg-rose-500/15",    border: "border-rose-500/40",    text: "text-rose-200",    label: "HIGH" },
  medium: { bg: "bg-amber-500/15",   border: "border-amber-500/40",   text: "text-amber-200",   label: "MED" },
  low:    { bg: "bg-slate-500/15",   border: "border-slate-500/40",   text: "text-slate-300",   label: "LOW" },
};

const KIND_LABEL = {
  future_date:        "Future date",
  ancient_date:       "Ancient date",
  bad_date_format:    "Bad date format",
  before_dob:         "Before DOB",
  duplicate_same_date:"Duplicate same date",
  duplicate_athlete: "Duplicate athlete",
  duplicate_baseline_signature: "Duplicate athlete (baseline)",
  duplicate_lab_content: "Duplicate lab content",
  impossible_value:   "Impossible value",
  impossible_height:  "Impossible height",
  impossible_weight:  "Impossible weight",
  big_swing:          "Big swing",
  big_weight_change:  "Big weight change",
  big_height_change:  "Big height change",
  height_reduction:   "Height reduction",
  missing_dob:        "Missing DOB",
  missing_sex:        "Missing gender",
  no_records:         "No records",
};

/**
 * Data Quality Detector modal — surfaces every record that looks like a
 * data-entry error (future dates, impossible values, big swings, duplicates,
 * structural gaps) so an admin can review and correct.
 */
export default function DataQualityModal({ open, onClose }) {
  useEscClose(open, onClose);
  const { isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [sevFilter, setSevFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  // Finding whose "Fix" button the admin clicked — the inline correction
  // modal reads its value/suggestion/marker off this object.
  const [fixing, setFixing] = useState(null);
  const [fixedIds, setFixedIds] = useState(new Set());
  // One-shot Mongo repair for legacy records that landed without a
  // `type` field (see /api/admin/repair-record-types). Kept alongside
  // the DQ report so admins have a single "fix common data issues"
  // surface — the repair endpoint is idempotent so tapping it twice
  // just reports "0 repaired". Result banner disappears on the next
  // load / manual dismiss.
  const [repairRunning, setRepairRunning] = useState(false);
  const [repairResult, setRepairResult]   = useState(null);
  const [repairError, setRepairError]     = useState("");

  const load = () => {
    setLoading(true);
    api.get("/admin/data-quality")
      .then((r) => setReport(r.data))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (open) load(); }, [open]);

  const filtered = useMemo(() => {
    if (!report) return [];
    return report.findings.filter((f) => {
      if (fixedIds.has(findingKey(f))) return false;
      if (sevFilter && f.severity !== sevFilter) return false;
      if (kindFilter && f.kind !== kindFilter) return false;
      return true;
    });
  }, [report, sevFilter, kindFilter, fixedIds]);

  const allKinds = useMemo(() => {
    if (!report) return [];
    return Array.from(new Set(report.findings.map((f) => f.kind))).sort();
  }, [report]);

  // Prints the currently-filtered findings (respects both filters + already-
  // fixed rows so admins can print a "HIGH only" report or "everything left
  // to review"). Uses the same body-class + @media print pattern as the KPI
  // drill-down modal (see index.css `body.dq-printing`).
  const handlePrint = () => {
    document.body.classList.add("dq-printing");
    setTimeout(() => {
      window.print();
      setTimeout(() => document.body.classList.remove("dq-printing"), 1000);
    }, 50);
  };

  // Backfills the `type` field on legacy records that landed in Mongo
  // without one (e.g. early-2025 spreadsheet imports). Idempotent: if
  // everything is already typed, the endpoint reports 0 repaired. On
  // success we also refresh the DQ report because the migration might
  // cascade-fix a few "no records" / mis-classified findings.
  const handleRepairTypes = async () => {
    if (!(await confirmDialog(
      "Run the record-type repair? This backfills a missing `type` on any legacy record "
      + "(inferred from the record's contents). Safe to run multiple times — it only touches "
      + "records that need it.",
      { confirmLabel: "Run repair" },
    ))) return;
    setRepairRunning(true);
    setRepairError("");
    setRepairResult(null);
    try {
      const r = await api.post("/admin/repair-record-types");
      setRepairResult(r.data);
      // Re-scan so any findings that were caused by mis-typed records
      // drop off the list.
      load();
    } catch (e) {
      setRepairError(e?.response?.data?.detail || e?.message || "Repair failed");
    } finally {
      setRepairRunning(false);
    }
  };

  // Mark a finding as reviewed / verified — persisted in Mongo via the
  // /admin/data-quality/ack endpoint so it won't reappear on the next
  // scan. Optimistically drops the row from the current view too; a
  // full re-scan happens on next modal open. If the ack fails (network
  // hiccup) the row snaps back on the follow-up load().
  const handleAckFinding = async (f) => {
    const note = await promptDialog(
      "Mark this finding as verified / OK.\n\n"
      + "You can add a short note explaining why (optional):",
      "",
      { confirmLabel: "Mark verified", placeholder: "Optional note" },
    );
    if (note === null) return; // user cancelled
    try {
      await api.post("/admin/data-quality/ack", {
        finding_key: f.finding_key,
        finding_kind: f.kind,
        sailor_id: f.sailor_id,
        sailor_name: f.sailor_name,
        message: f.message,
        note: note || "",
      });
      // Optimistic client-side removal so the row disappears instantly.
      setReport((cur) => {
        if (!cur) return cur;
        const kept = (cur.findings || []).filter((x) => x.finding_key !== f.finding_key);
        return { ...cur, findings: kept, total: kept.length };
      });
    } catch (e) {
      alertDialog(e?.response?.data?.detail || e?.message || "Could not mark verified");
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4 dq-modal-root"
      data-testid="data-quality-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="card w-full max-w-6xl p-5 dq-modal-card">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-rose-300 flex items-center gap-1.5">
              <AlertTriangle size={11} /> Data quality check
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              {report ? `${report.total} finding${report.total === 1 ? "" : "s"}` : "Scanning…"}
            </div>
            {report && (
              <div className="text-[11px] text-slate-500 mt-0.5 flex gap-2">
                <span className="text-rose-300">HIGH · {report.counts.high || 0}</span>
                <span className="text-slate-600">·</span>
                <span className="text-amber-300">MED · {report.counts.medium || 0}</span>
                <span className="text-slate-600">·</span>
                <span className="text-slate-400">LOW · {report.counts.low || 0}</span>
                <span className="text-slate-600">·</span>
                <span className="text-slate-500">generated {new Date(report.generated_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2" data-no-print="true">
            {isAdmin && (
              <button
                onClick={handleRepairTypes}
                disabled={repairRunning}
                className="pill !text-[11px] disabled:opacity-50"
                title="Backfills a missing `type` field on legacy records (idempotent — safe to run multiple times). Fixes the 'history not showing' issue after a fresh deploy to a new environment."
                data-testid="dq-repair-types-btn"
              >
                {repairRunning
                  ? <><Loader2 size={12} className="animate-spin" /> Repairing…</>
                  : <><Tags size={12} /> Repair record types</>}
              </button>
            )}
            {report && report.total > 0 && (
              <button
                onClick={handlePrint}
                className="pill !text-[11px]"
                title="Print the currently-filtered findings"
                data-testid="dq-print-btn"
              >
                <Printer size={12} /> Print PDF
              </button>
            )}
            <button onClick={onClose} className="pill !p-1.5" data-testid="dq-close">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Repair-types result banner — persists until the modal is
            re-opened or the admin dismisses it. */}
        {(repairResult || repairError) && (
          <div className="mb-3" data-testid="dq-repair-banner">
            {repairError ? (
              <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-[12px] flex items-start justify-between gap-2">
                <div>
                  <span className="font-semibold">Repair failed:</span> {repairError}
                </div>
                <button className="text-rose-300 hover:text-rose-100 shrink-0" onClick={() => setRepairError("")}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 px-3 py-2 text-[12px] flex items-start justify-between gap-2"
                   data-testid="dq-repair-success">
                <div>
                  <span className="font-semibold">Repair complete.</span>{" "}
                  {repairResult.total_repaired === 0
                    ? <>Nothing to fix — all records already carry a valid type.</>
                    : <>Backfilled <span className="font-mono-data">{repairResult.total_repaired}</span> record{repairResult.total_repaired === 1 ? "" : "s"} across{" "}
                        <span className="font-mono-data">{repairResult.athletes_touched}</span> athlete{repairResult.athletes_touched === 1 ? "" : "s"} —{" "}
                        <span className="font-mono-data">{repairResult.records_repaired?.lab || 0}</span> as labs,{" "}
                        <span className="font-mono-data">{repairResult.records_repaired?.consultation || 0}</span> as consultations,{" "}
                        <span className="font-mono-data">{repairResult.records_repaired?.biometric || 0}</span> as biometrics.</>}
                </div>
                <button className="text-emerald-300 hover:text-emerald-100 shrink-0" onClick={() => setRepairResult(null)}>
                  <X size={13} />
                </button>
              </div>
            )}
          </div>
        )}

        {loading && !report ? (
          <div className="py-12 text-center text-slate-500 text-sm">
            <Loader2 size={16} className="animate-spin inline mr-2" /> Scanning all records…
          </div>
        ) : !report ? (
          <div className="rounded-lg border border-dashed border-rose-700 p-6 text-center text-sm text-rose-300">
            Could not load data quality report. You may not have admin permission.
          </div>
        ) : report.total === 0 ? (
          <div className="rounded-lg border border-emerald-700 bg-emerald-500/10 p-6 text-center text-sm text-emerald-200 flex items-center justify-center gap-2">
            <CheckCircle2 size={16} /> All records look clean. No anomalies detected.
          </div>
        ) : (
          <>
            {/* Filter strip */}
            <div className="flex items-center gap-2 mb-3 text-[11px] flex-wrap" data-no-print="true">
              <Filter size={11} className="text-slate-500" />
              <select
                value={sevFilter}
                onChange={(e) => setSevFilter(e.target.value)}
                className="form-input !py-1 !px-2 !text-[11px]"
                data-testid="dq-sev-filter"
              >
                <option value="">All severities</option>
                <option value="high">High only</option>
                <option value="medium">Medium only</option>
                <option value="low">Low only</option>
              </select>
              <select
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value)}
                className="form-input !py-1 !px-2 !text-[11px]"
                data-testid="dq-kind-filter"
              >
                <option value="">All types</option>
                {allKinds.map((k) => <option key={k} value={k}>{KIND_LABEL[k] || k}</option>)}
              </select>
              <span className="text-slate-500 ml-auto">Showing {filtered.length} of {report.total}</span>
            </div>

            {/* Print-only header: printed at the top of the paper output so the
                reader knows the filter context + total counts. Hidden on-screen
                via `hidden print:block` (Tailwind print variant). */}
            <div
              className="hidden print:block mb-3 text-[11px] text-slate-700"
              data-testid="dq-print-header"
            >
              <div className="font-semibold text-slate-900">
                Data-quality findings ({filtered.length} shown)
              </div>
              <div className="mt-0.5">
                HIGH {report.counts.high || 0} · MED {report.counts.medium || 0} · LOW {report.counts.low || 0}
                {sevFilter && ` · filter: ${sevFilter}`}
                {kindFilter && ` · type: ${KIND_LABEL[kindFilter] || kindFilter}`}
                {" · generated "}
                {new Date(report.generated_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>

            <div className="overflow-auto dq-scroll" style={{ maxHeight: 560 }}>
              <table className="w-full text-[11px] dq-table">
                <thead className="sticky top-0 bg-[#0B1627] z-10">
                  <tr className="text-slate-400 uppercase tracking-wider text-[9px]">
                    <th className="text-left px-2 py-2 font-normal">Sev</th>
                    <th className="text-left px-2 py-2 font-normal">Type</th>
                    <th className="text-left px-2 py-2 font-normal">Athlete</th>
                    <th className="text-left px-2 py-2 font-normal">Date</th>
                    <th className="text-left px-2 py-2 font-normal">Detail</th>
                    <th className="text-right px-2 py-2 font-normal" data-no-print="true">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((f, i) => {
                    const sev = SEV_STYLE[f.severity] || SEV_STYLE.low;
                    return (
                      <tr
                        key={`${findingKey(f)}|${i}`}
                        className="border-t border-[#1E304B] hover:bg-slate-900/40"
                        data-testid={`dq-row-${i}`}
                      >
                        <td className="px-2 py-1.5">
                          <span className={`dq-sev-chip inline-block px-1.5 py-0.5 rounded border ${sev.bg} ${sev.border} ${sev.text} font-mono-data text-[9px]`}>
                            {sev.label}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-slate-400 whitespace-nowrap">
                          {KIND_LABEL[f.kind] || f.kind}
                        </td>
                        <td className="px-2 py-1.5 text-slate-200 whitespace-nowrap">
                          {f.sailor_name}
                        </td>
                        <td className="px-2 py-1.5 text-slate-500 font-mono-data whitespace-nowrap">
                          {fmtDate(f.date)}
                        </td>
                        <td className="px-2 py-1.5 text-slate-300">
                          {f.message}
                          {f.suggestion && (
                            <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 font-mono-data text-[10px]">
                              → {isFindingDateFix(f) ? fmtDate(f.suggestion) : f.suggestion}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-right whitespace-nowrap" data-no-print="true">
                          {isFixableFinding(f) && (
                            <button
                              className="pill !text-[10px] !py-1 !px-2"
                              onClick={() => setFixing(f)}
                              title="Apply the suggested correction"
                              data-testid={`dq-fix-${i}`}
                            >
                              <Wrench size={10} /> Fix
                            </button>
                          )}
                          <button
                            className="pill !text-[10px] !py-1 !px-2 ml-1"
                            onClick={() => handleAckFinding(f)}
                            title="Reviewed and this is correct — don't show it again"
                            data-testid={`dq-ack-${i}`}
                          >
                            <CheckCircle2 size={10} /> Verified
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {fixing && (
          fixing.kind === "duplicate_same_date"
            ? <ResolveDuplicateModal
                finding={fixing}
                onClose={() => setFixing(null)}
                onApplied={() => {
                  setFixedIds((prev) => new Set(prev).add(findingKey(fixing)));
                  setFixing(null);
                  setTimeout(load, 200);
                }}
              />
            : fixing.kind === "duplicate_athlete"
              ? <MergeAthletesModal
                  finding={fixing}
                  onClose={() => setFixing(null)}
                  onApplied={() => {
                    setFixedIds((prev) => new Set(prev).add(findingKey(fixing)));
                    setFixing(null);
                    setTimeout(load, 200);
                  }}
                />
              : <FixOneModal
                  finding={fixing}
                  onClose={() => setFixing(null)}
                  onApplied={() => {
                    setFixedIds((prev) => new Set(prev).add(findingKey(fixing)));
                    setFixing(null);
                    setTimeout(load, 200);
                  }}
                />
        )}
      </div>
    </div>,
    document.body,
  );
}

// Unique key per finding so we can hide fixed rows before the next scan
// finishes returning.
function findingKey(f) {
  return `${f.sailor_id}|${f.record_id || ""}|${f.kind}|${f.marker || f.field || ""}|${f.date || ""}`;
}

// A finding is a "date fix" (its `suggestion` is an ISO date, not a number)
// when the kind is one of the date-anomaly kinds. Kept next to the fix modal
// which uses the same check.
function isFindingDateFix(f) {
  return f.kind === "future_date" || f.kind === "ancient_date" || f.kind === "before_dob";
}

// Whether a finding has enough info for an inline single-click correction.
function isFixableFinding(f) {
  if (!f.sailor_id) return false;
  if (f.kind === "duplicate_same_date") {
    return Array.isArray(f.record_ids) && f.record_ids.length >= 2;
  }
  if (f.kind === "duplicate_athlete") {
    return Array.isArray(f.sailor_ids) && f.sailor_ids.length >= 2;
  }
  if (!f.record_id) return false;
  if (f.kind === "impossible_value") return true;
  if (f.kind === "impossible_height" || f.kind === "impossible_weight") return true;
  if (f.kind === "height_reduction" || f.kind === "big_height_change" || f.kind === "big_weight_change") return true;
  if (f.kind === "future_date" || f.kind === "ancient_date" || f.kind === "before_dob") return true;
  if (f.kind === "big_swing") return true;   // let admin correct the swing endpoint
  return false;
}

/**
 * Inline "Fix one" confirmation modal. Pre-fills the input with the
 * server-suggested correction (when present) but lets the admin type any
 * value. Applies via `POST /api/admin/data-quality/apply-fix` and shows a
 * success toast before dismissing itself.
 */
function FixOneModal({ finding, onClose, onApplied }) {
  useEscClose(true, onClose);
  const isValueFix = finding.kind === "impossible_value" || finding.kind === "big_swing";
  const isHeightFix = finding.kind === "impossible_height"
    || finding.kind === "height_reduction"
    || finding.kind === "big_height_change";
  const isWeightFix = finding.kind === "impossible_weight" || finding.kind === "big_weight_change";
  const isDateFix = finding.kind === "future_date" || finding.kind === "ancient_date" || finding.kind === "before_dob";

  const initial = finding.suggestion ?? (isDateFix ? finding.date : String(finding.value ?? ""));
  const [val, setVal] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const label =
      isValueFix  ? `${(finding.marker || "").toUpperCase()} (${finding.unit || ""})`
    : isHeightFix ? "Height (cm)"
    : isWeightFix ? "Weight (kg)"
    : isDateFix   ? "Corrected date (YYYY-MM-DD)"
    : "New value";

  const apply = async () => {
    if (val === "" || val === null || val === undefined) { setErr("Enter a value"); return; }
    setSaving(true); setErr("");
    try {
      const body = {
        sailor_id: finding.sailor_id,
        record_id: finding.record_id,
        new_value: isDateFix ? String(val) : Number(val),
      };
      if (isValueFix) body.param_key = finding.marker;
      else if (isHeightFix) body.field = "height_cm";
      else if (isWeightFix) body.field = "weight_kg";
      else if (isDateFix) body.field = "date";
      await api.post("/admin/data-quality/apply-fix", body);
      onApplied?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Fix failed");
    } finally { setSaving(false); }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
      data-testid="dq-fix-one-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="card w-full max-w-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1.5">
              <Wrench size={11} /> Apply correction
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              {finding.sailor_name} · {fmtDate(finding.date)}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{finding.message}</div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="dq-fix-close">
            <X size={14} />
          </button>
        </div>

        {err && (
          <div className="card !bg-rose-500/10 !border-rose-500/30 px-3 py-2 text-sm text-rose-200 mb-3 flex items-center gap-2">
            <AlertTriangle size={14} /> {err}
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500">Current</label>
              <div className="mt-1 rounded-lg border border-[#1E304B] bg-[#0E1A2E] px-3 py-2 font-mono-data text-slate-300 text-sm">
                {isDateFix ? fmtDate(finding.date) : (finding.value ?? "—")}
              </div>
            </div>
            <div className="text-slate-500 text-lg pt-4">→</div>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-emerald-300">{label}</label>
              <input
                type={isDateFix ? "date" : "number"}
                value={val}
                step={isDateFix ? undefined : "any"}
                onChange={(e) => setVal(e.target.value)}
                className="form-input mt-1 w-full no-spinner"
                data-testid="dq-fix-input"
                autoFocus
              />
              {finding.suggestion && String(val) !== String(finding.suggestion) && (
                <button
                  className="mt-1 text-[10px] text-cyan-300 hover:underline flex items-center gap-1"
                  onClick={() => setVal(finding.suggestion)}
                  data-testid="dq-fix-reset-suggestion"
                >
                  <Undo2 size={10} /> Reset to suggestion ({isDateFix ? fmtDate(finding.suggestion) : finding.suggestion})
                </button>
              )}
            </div>
          </div>
          {finding.prev_date && finding.prev_value != null && (
            <div className="text-[10px] text-slate-500 font-mono-data border-t border-[#1E304B] pt-2">
              Previous value {finding.prev_value} on {fmtDate(finding.prev_date)}
              {finding.marker && ` · marker ${finding.marker.toUpperCase()}`}
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="pill" data-testid="dq-fix-cancel">Cancel</button>
          <button onClick={apply} disabled={saving} className="pill active disabled:opacity-60"
                  data-testid="dq-fix-apply">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            {saving ? "Applying…" : "Apply fix"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}


/**
 * Duplicate-resolution picker. Fetches all N duplicate records for the athlete,
 * previews their params side by side, lets the operator pick which ONE to keep
 * (auto-picks the "richest" record by default), and cascades a delete of the
 * others via `POST /admin/data-quality/resolve-duplicate`.
 *
 * Deleted records are still recoverable from the Archive (each delete emits an
 * audit row) so an over-eager click can be walked back.
 */
function ResolveDuplicateModal({ finding, onClose, onApplied }) {
  useEscClose(true, onClose);
  const [records, setRecords] = useState(null);   // null = loading
  const [keepId, setKeepId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get(`/sailors/${finding.sailor_id}`)
      .then((r) => {
        if (cancelled) return;
        const byId = new Map((r.data?.records || []).map((rec) => [rec.id, rec]));
        const dupes = (finding.record_ids || [])
          .map((id) => byId.get(id))
          .filter(Boolean);
        setRecords(dupes);
        // Auto-select the "richest" — most non-null params, tiebreak on latest last_saved_at.
        // Mirrors the backend's _richness_score() so the default matches what the auto-mode
        // endpoint would pick.
        const scored = dupes.map((rec) => {
          const params = rec.params || {};
          const nonNull = Object.values(params).filter((v) => v != null && v !== "").length;
          const topFields = ["dr", "hospital", "advice", "diagnosis", "pdf_file_id"]
            .filter((k) => rec[k]).length;
          const ts = rec.last_saved_at || rec.date || "";
          return { id: rec.id, score: [nonNull, topFields, ts] };
        });
        scored.sort((a, b) => {
          for (let i = 0; i < 3; i++) {
            if (a.score[i] < b.score[i]) return 1;
            if (a.score[i] > b.score[i]) return -1;
          }
          return 0;
        });
        setKeepId(scored[0]?.id || dupes[0]?.id);
      })
      .catch((e) => setErr(e?.response?.data?.detail || e?.message || "Could not load duplicates"));
    return () => { cancelled = true; };
  }, [finding.sailor_id, finding.record_ids]);

  const apply = async (autoMode) => {
    setSaving(true);
    setErr("");
    try {
      await api.post("/admin/data-quality/resolve-duplicate", {
        sailor_id: finding.sailor_id,
        record_ids: finding.record_ids,
        keep_record_id: autoMode ? null : keepId,
      });
      onApplied();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Resolve failed");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[220] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
         data-testid="dq-resolve-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="card w-full max-w-3xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
              <Wrench size={11} /> Resolve duplicates
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              {finding.sailor_name} · {fmtDate(finding.date)}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{finding.message}</div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="dq-resolve-close">
            <X size={14} />
          </button>
        </div>

        {err && (
          <div className="card !bg-rose-500/10 !border-rose-500/30 px-3 py-2 text-sm text-rose-200 mb-3 flex items-center gap-2">
            <AlertTriangle size={14} /> {err}
          </div>
        )}

        {records == null ? (
          <div className="py-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={14} /> Loading duplicates…
          </div>
        ) : (
          <>
            <div className="text-[11px] text-slate-400 mb-2">
              Pick the record to <strong className="text-emerald-300">KEEP</strong>. The other{" "}
              {records.length - 1} will be deleted (recoverable from the Archive).
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {records.map((rec, i) => {
                const params = rec.params || {};
                const nonNullCount = Object.values(params).filter((v) => v != null && v !== "").length;
                const preview = Object.entries(params)
                  .filter(([, v]) => v != null && v !== "")
                  .slice(0, 6)
                  .map(([k, v]) => `${k.toUpperCase()} ${v}`)
                  .join(" · ");
                const isKeep = keepId === rec.id;
                return (
                  <label
                    key={rec.id}
                    className={`block rounded-lg border p-3 cursor-pointer ${
                      isKeep
                        ? "border-emerald-500/60 bg-emerald-500/5"
                        : "border-[#1E304B] bg-[#0E1A2E] hover:border-slate-400"
                    }`}
                    data-testid={`dq-resolve-option-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="keep-record"
                        checked={isKeep}
                        onChange={() => setKeepId(rec.id)}
                        className="mt-0.5"
                        data-testid={`dq-resolve-radio-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[11px] font-mono-data">
                          <span className={isKeep ? "text-emerald-300 font-semibold" : "text-slate-400"}>
                            {isKeep ? "KEEP" : `DELETE`}
                          </span>
                          <span className="text-slate-500">·</span>
                          <span className="text-slate-300">{rec.type || "lab"}</span>
                          {rec.hospital && (
                            <><span className="text-slate-600">·</span><span className="text-slate-400">{rec.hospital}</span></>
                          )}
                          {rec.dr && (
                            <><span className="text-slate-600">·</span><span className="text-slate-400">Dr {rec.dr}</span></>
                          )}
                          <span className="ml-auto text-slate-500">
                            {nonNullCount} value{nonNullCount === 1 ? "" : "s"}
                          </span>
                        </div>
                        {preview && (
                          <div className="text-[11px] font-mono-data text-slate-400 mt-1 truncate">
                            {preview}
                            {Object.keys(params).length > 6 && (
                              <span className="text-slate-600"> · +{Object.keys(params).length - 6} more</span>
                            )}
                          </div>
                        )}
                        {(rec.advice || rec.diagnosis) && (
                          <div className="text-[10px] text-slate-500 mt-1 truncate">
                            {rec.diagnosis && <span className="text-slate-400">{rec.diagnosis} · </span>}
                            {rec.advice}
                          </div>
                        )}
                        <div className="text-[10px] text-slate-500 mt-1 font-mono-data">
                          {rec.last_saved_at
                            ? <>saved {rec.last_saved_at.slice(0, 16).replace("T", " ")}
                                {rec.last_saved_by?.name && <> by {rec.last_saved_by.name}</>}</>
                            : <span className="text-slate-600">no save timestamp</span>}
                          {rec.pdf_file_id && <span className="ml-2 text-cyan-400">📎 PDF attached</span>}
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                onClick={() => apply(true)}
                disabled={saving || records.length < 2}
                className="pill disabled:opacity-40"
                title="Server auto-picks the richest record; deletes the rest."
                data-testid="dq-resolve-auto"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                Auto-pick richest
              </button>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="pill" data-testid="dq-resolve-cancel">Cancel</button>
                <button
                  onClick={() => apply(false)}
                  disabled={saving || !keepId || records.length < 2}
                  className="pill active disabled:opacity-40"
                  data-testid="dq-resolve-apply"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                  Delete {records.length - 1} extras
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}



/**
 * Athlete-merge picker for `duplicate_athlete` findings. Loads every
 * candidate athlete (each carries its own records/baseline), lets the
 * operator pick which one to KEEP, and cascades a merge via
 * `POST /admin/data-quality/merge-athletes` that:
 *   • unions records into the keeper (backup wins on same-id collisions)
 *   • adopts the loser's baseline only if keeper has none
 *   • repoints audit_log so restore-from-Archive stays coherent
 *   • deletes the losing athlete rows
 *
 * Every merge writes one audit row per loser — the Archive keeps the
 * loser's full document so a mis-merge can still be reversed.
 */
function MergeAthletesModal({ finding, onClose, onApplied }) {
  useEscClose(true, onClose);
  const [athletes, setAthletes] = useState(null);
  const [keepId, setKeepId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    Promise.all(
      (finding.sailor_ids || []).map((id) => api.get(`/sailors/${id}`).then((r) => r.data)),
    ).then((rows) => {
      if (cancelled) return;
      const kept = rows.filter(Boolean);
      setAthletes(kept);
      // Default: pick the athlete with the MOST records (usually the "real" one).
      const scored = kept.map((a) => ({
        id: a.id,
        score: (a.records?.length || 0) * 10 + (a.baseline && Object.keys(a.baseline).length ? 1 : 0),
      }));
      scored.sort((x, y) => y.score - x.score);
      setKeepId(scored[0]?.id || kept[0]?.id);
    }).catch((e) => setErr(e?.response?.data?.detail || e?.message || "Could not load athletes"));
    return () => { cancelled = true; };
  }, [finding.sailor_ids]);

  const apply = async () => {
    if (!keepId || !athletes || athletes.length < 2) return;
    setSaving(true);
    setErr("");
    try {
      await api.post("/admin/data-quality/merge-athletes", {
        keep_sailor_id: keepId,
        merge_sailor_ids: athletes.filter((a) => a.id !== keepId).map((a) => a.id),
      });
      // Also notify the parent shell so the roster reloads.
      window.dispatchEvent(new CustomEvent("ych:data-changed"));
      onApplied();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Merge failed");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[220] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
         data-testid="dq-merge-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="card w-full max-w-3xl p-5 md:p-6">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
              <Wrench size={11} /> Merge duplicate athletes
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              {finding.sailor_name}
            </div>
            <div className="text-[11px] text-slate-500 mt-0.5">{finding.message}</div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="dq-merge-close">
            <X size={14} />
          </button>
        </div>

        {err && (
          <div className="card !bg-rose-500/10 !border-rose-500/30 px-3 py-2 text-sm text-rose-200 mb-3 flex items-center gap-2">
            <AlertTriangle size={14} /> {err}
          </div>
        )}

        {athletes == null ? (
          <div className="py-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="animate-spin" size={14} /> Loading athletes…
          </div>
        ) : (
          <>
            <div className="text-[11px] text-slate-400 mb-3">
              Pick the athlete row to <strong className="text-emerald-300">KEEP</strong>.
              The other {athletes.length - 1} will be absorbed — their records union into the
              keeper (records with matching IDs prefer the keeper&apos;s values), baseline is
              adopted only if the keeper doesn&apos;t have one, then the losing row is deleted.
              Losing rows stay recoverable from the Archive.
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
              {athletes.map((a, i) => {
                const isKeep = keepId === a.id;
                const dob = a.baseline?.dob || null;
                const gender = a.baseline?.gender || a.baseline?.sex || null;
                const recCount = a.records?.length || 0;
                const labCount = (a.records || []).filter((r) => (r.type || "lab") === "lab").length;
                const bioCount = (a.records || []).filter((r) => r.type === "biometric").length;
                const baselineFilled = a.baseline ? Object.keys(a.baseline).length : 0;
                return (
                  <label
                    key={a.id}
                    className={`block rounded-lg border p-3 cursor-pointer ${
                      isKeep
                        ? "border-emerald-500/60 bg-emerald-500/5"
                        : "border-[#1E304B] bg-[#0E1A2E] hover:border-slate-400"
                    }`}
                    data-testid={`dq-merge-option-${i}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        name="keep-athlete"
                        checked={isKeep}
                        onChange={() => setKeepId(a.id)}
                        className="mt-0.5"
                        data-testid={`dq-merge-radio-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-[12px]">
                          <span className={isKeep ? "text-emerald-300 font-semibold" : "text-slate-400 uppercase text-[10px] font-mono-data"}>
                            {isKeep ? "KEEP" : "ABSORB"}
                          </span>
                          <span className="text-slate-500">·</span>
                          <span className="text-slate-100 font-display">{a.name}</span>
                          <span className="text-slate-500 text-[11px]">
                            {a.group}{a.subgroup ? ` · ${a.subgroup}` : ""}
                          </span>
                          <span className="ml-auto text-[10px] text-slate-500 font-mono-data">
                            id {a.id.slice(0, 8)}
                          </span>
                        </div>
                        <div className="text-[11px] font-mono-data text-slate-400 mt-1">
                          <strong className="text-slate-200">{recCount}</strong> records
                          {" · "}<strong>{labCount}</strong> labs
                          {" · "}<strong>{bioCount}</strong> biometrics
                          {baselineFilled > 0 && <> · baseline <strong>{baselineFilled}</strong> fields</>}
                        </div>
                        <div className="text-[10px] text-slate-500 mt-1 font-mono-data">
                          DOB {dob ? fmtDate(dob) : "—"}
                          {" · "}Gender {gender || "—"}
                          {a.baseline_updated_at && <> · baseline saved {a.baseline_updated_at.slice(0, 10)}</>}
                        </div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="mt-4 flex items-center justify-between gap-2">
              <div className="text-[10px] text-slate-500">
                Merges are logged — a losing athlete can be restored from the Archive.
              </div>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="pill" data-testid="dq-merge-cancel">Cancel</button>
                <button
                  onClick={apply}
                  disabled={saving || !keepId || athletes.length < 2}
                  className="pill active disabled:opacity-40"
                  data-testid="dq-merge-apply"
                >
                  {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                  Merge {athletes.length - 1} into keeper
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

